import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEqual, countBy } from 'lodash'
import {
  RolloverAccountStatus,
  ServicesAgreementParticipantsTooltip,
} from 'config/automatic-rollovers'
import {
  sanitizeForSearch,
  formatDate,
  dateSorter,
  formatCurrency,
  displayAttribute,
  numberSorter,
} from 'utils'
import {
  Filter,
  PaginatedAndSortableTable,
  ComponentFailure,
  Spinner,
  QuickFilterCard,
  SearchInput,
  TableHeading,
  NoTableResults,
  DownloadTableButton,
  StatusBadge,
  TableHeader,
  Tooltip,
} from 'components'
import { Icon as SuccessIcon } from 'images/status-icons/checkmark-green.svg'
import { Icon as ChecklistIcon } from 'images/status-icons/checklist.svg'
import { Icon as ClockIcon } from 'images/status-icons/clock-new.svg'
import { ParticipantsFilterDialog } from '../components'
import { ProductAgreementType } from '../../../config/products'
import * as Types from 'types'
import { TableColumn } from 'lp-components'
import { compose } from 'redux'

const propTypes = {
  participants: PropTypes.arrayOf(Types.rolloverAccount),
  isFetching: PropTypes.bool.isRequired,
  baseFilters: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  filters: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  handleFilter: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  handleSearch: PropTypes.func.isRequired,
  fieldMappings: PropTypes.arrayOf(PropTypes.object).isRequired,
  fileName: PropTypes.string.isRequired,
  fetchServicesAgreementParticipants: PropTypes.func.isRequired,
  servicesAgreementId: PropTypes.string.isRequired,
}

const defaultProps = {
  participants: null,
}
const TABLE_ID = 'agreements-participants-table'

const ParticipantsHeader = ({ children }) => {
  return (
    <div className="services-agreement-participants-header">
      <h2>Participants</h2>
      {children}
    </div>
  )
}
ParticipantsHeader.propTypes = {
  children: PropTypes.node,
}
ParticipantsHeader.defaultProps = {
  children: null,
}

const StatusColumn = ({ data, value }) => {
  const { status } = data
  const tooltipContent = ServicesAgreementParticipantsTooltip[status]
  return (
    <td className="status">
      {status ? (
        <Tooltip
          offset={[0, 18]}
          content={tooltipContent}
          ariaLabel={tooltipContent}
        >
          <StatusBadge status={status} />
        </Tooltip>
      ) : (
        <span>{value}</span>
      )}
    </td>
  )
}
StatusColumn.propTypes = {
  data: PropTypes.object.isRequired,
  value: PropTypes.string,
}
StatusColumn.defaultProps = {
  value: '',
}

function ServicesAgreementParticipantsView({
  participants,
  fetchServicesAgreementParticipants,
  servicesAgreementId,
  fieldMappings,
  search,
  handleSearch,
  isFetching,
  handleFilter,
  filters,
  baseFilters,
  fileName,
}) {
  const [errorExists, setErrorExists] = useState(false)
  const [showFilterModal, setShowFilterModal] = useState(false)
  const hasFiltersApplied = !isEqual(filters, baseFilters)
  const noServicesAgreementParticipants = !participants?.length
  const AccountStatus = RolloverAccountStatus

  useEffect(() => {
    async function getServicesAgreementParticipants() {
      try {
        await fetchServicesAgreementParticipants(servicesAgreementId)
      } catch {
        setErrorExists(true)
      }
    }
    // will need to add trigger-refetch logic
    if (!participants) getServicesAgreementParticipants()
  }, [participants, fetchServicesAgreementParticipants, servicesAgreementId])

  const toggleFilter = (filterLabel) => {
    handleFilter({
      status: filters.status.includes(filterLabel)
        ? filters.status.filter((status) => status !== filterLabel)
        : filters.status.concat(filterLabel),
    })
  }

  const counts = countBy(participants, 'status')
  const filteredParticipants = useMemo(() => {
    if (!participants) return
    let searchResults = participants

    const sanitizedValue = sanitizeForSearch(search)
    if (sanitizedValue !== '') {
      searchResults = searchResults.filter(({ participantName }) =>
        sanitizeForSearch(participantName).includes(sanitizedValue)
      )
    }

    if (hasFiltersApplied) {
      return searchResults.filter(({ status }) =>
        filters.status.includes(status)
      )
    }

    return searchResults
  }, [search, participants, filters, hasFiltersApplied])

  if (isFetching)
    return (
      <div className="services-agreement-participants">
        <ParticipantsHeader />
        <Spinner />
      </div>
    )

  if (errorExists)
    return (
      <div className="services-agreement-participants">
        <ParticipantsHeader />
        <ComponentFailure />
      </div>
    )
  const disabled = !filteredParticipants?.length
  const showIcon = disabled

  return (
    <div className="services-agreement-participants">
      <div className="quick-filters-container">
        <div className="container">
          <div className="flex-columns">
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(
                  AccountStatus.ROLLOVER_COMPLETE
                )}
                count={counts[AccountStatus.ROLLOVER_COMPLETE] ?? 0}
                label={AccountStatus.ROLLOVER_COMPLETE}
                icon={SuccessIcon}
                onClick={() => toggleFilter(AccountStatus.ROLLOVER_COMPLETE)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(
                  AccountStatus.READY_FOR_FUNDING
                )}
                count={counts[AccountStatus.READY_FOR_FUNDING] ?? 0}
                label={AccountStatus.READY_FOR_FUNDING}
                icon={ChecklistIcon}
                onClick={() => toggleFilter(AccountStatus.READY_FOR_FUNDING)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(AccountStatus.PROCESSING)}
                count={counts[AccountStatus.PROCESSING] ?? 0}
                label={AccountStatus.PROCESSING}
                icon={ClockIcon}
                onClick={() => toggleFilter(AccountStatus.PROCESSING)}
              />
            </div>
          </div>
        </div>
      </div>
      <TableHeading title="Participants">
        {!noServicesAgreementParticipants && (
          <DownloadTableButton
            data={filteredParticipants}
            fieldMappings={fieldMappings}
            fileName={fileName}
            disabled={disabled}
            showIcon={showIcon}
          />
        )}
        <div className="input-container">
          <SearchInput
            type="search"
            aria-label="Search participants"
            placeholder="Search participant name"
            onChange={(e) => handleSearch(e.target.value)}
            value={search}
          />
          <Filter
            toggleMenu={() => setShowFilterModal((s) => !s)}
            isActive={hasFiltersApplied}
          />
          {showFilterModal && (
            <ParticipantsFilterDialog
              filters={filters}
              agreementType={ProductAgreementType.ARO}
              handleSubmit={(filterValues) => handleFilter(filterValues)}
              clearAllFilters={() => handleFilter(baseFilters)}
              handleClose={() => setShowFilterModal(false)}
            />
          )}
        </div>
      </TableHeading>
      <PaginatedAndSortableTable
        tableId={TABLE_ID}
        data={filteredParticipants}
        initialAscending={false}
        initialColumn="createdDate"
        parentScrollContainerSelector="#main-content"
        className="services-agreements-table"
        noRowsFoundMessage={
          <NoTableResults
            title="No results found"
            details={
              noServicesAgreementParticipants ? (
                <>
                  When a participant is added, we'll send you a notification.
                  <br />
                  You could also try adjusting your filter or search.
                </>
              ) : (
                'Try adjusting your filter or search.'
              )
            }
          />
        }
      >
        <TableColumn name="participantName" label="Participant name" />
        <TableColumn name="accountTypeDescription" label="Account type" />
        <TableColumn
          name="status"
          label={'Status'}
          format={displayAttribute}
          component={StatusColumn}
        />
        <TableColumn
          name="createdDate"
          label={'Created date'}
          format={formatDate}
          sortFunc={dateSorter}
        />
        <TableColumn
          label={'Initial investment amount'}
          name="initialInvestmentAmount"
          format={compose(displayAttribute, formatCurrency)}
          className="currency"
          headerComponent={TableHeader}
          sortFunc={numberSorter}
        />
      </PaginatedAndSortableTable>
    </div>
  )
}
ServicesAgreementParticipantsView.propTypes = propTypes
ServicesAgreementParticipantsView.defaultProps = defaultProps

export default ServicesAgreementParticipantsView
